import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Live2DViewer from "../components/Live2DViewer";
import { useSequences } from "../hooks/useSequences";
import { useLessonFlow } from "../hooks/useLessonFlow";
import { useAuth } from "../contexts/AuthContext";

const baseCardStyle = {
  background: "rgba(15, 23, 42, 0.75)",
  borderRadius: "16px",
  padding: "16px",
  color: "#ffffff",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
  boxSizing: "border-box"
};

const titleStyle = {
  fontSize: "18px",
  fontWeight: 600,
  textAlign: "center",
  padding: "10px 18px",
  borderRadius: "999px",
  background:
    "linear-gradient(120deg, rgba(125,211,252,0.35), rgba(56,189,248,0.45))",
  border: "1px solid rgba(191,219,254,0.6)",
  color: "#e0f2fe",
  textShadow: "0 1px 4px rgba(14,116,144,0.45)",
  boxShadow: "0 6px 18px rgba(14,165,233,0.35)"
};

const sentenceStyle = {
  fontSize: "20px",
  lineHeight: 1.5,
  textAlign: "center",
  fontWeight: 600,
  padding: "16px",
  borderRadius: "20px",
  background:
    "linear-gradient(145deg, rgba(14,116,144,0.6), rgba(8,145,178,0.4))",
  border: "1px solid rgba(125,211,252,0.35)",
  boxShadow:
    "0 12px 40px rgba(14,165,233,0.35), inset 0 0 8px rgba(191,219,254,0.55)",
  color: "#f8fafc",
  textShadow: "0 2px 8px rgba(14,116,144,0.45)"
};

const bodyStyle = {
  fontSize: "15px",
  lineHeight: 1.5,
  whiteSpace: "pre-line"
};

const buttonBaseStyle = {
  flex: "1 1 100%",
  minWidth: "140px",
  padding: "12px 16px",
  fontSize: "16px",
  border: "none",
  borderRadius: "10px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
};

const buttonStyleMap = {
  primary: { ...buttonBaseStyle, backgroundColor: "#4CAF50", color: "#ffffff" },
  secondary: {
    ...buttonBaseStyle,
    backgroundColor: "#37474f",
    color: "#ffffff"
  },
  neutral: {
    ...buttonBaseStyle,
    backgroundColor: "#607d8b",
    color: "#ffffff"
  },
  primaryHalf: {
    ...buttonBaseStyle,
    backgroundColor: "#4CAF50",
    color: "#ffffff",
    flex: "1 1 calc(50% - 8px)",
    maxWidth: "calc(50% - 8px)"
  },
  secondaryHalf: {
    ...buttonBaseStyle,
    backgroundColor: "#37474f",
    color: "#ffffff",
    flex: "1 1 calc(50% - 8px)",
    maxWidth: "calc(50% - 8px)"
  }
};

const ACTIONS_TRIGGERING_AUTOPLAY = new Set(["replay_sentence"]);

// 除外する単語のセット（PronunciationLabPageから移植）
const EXCLUDED_WORDS = new Set([
  'a', 'an', 'the',
  'is', 'am', 'are', 'was',
  'to', 'in', 'on', 'at', 'of', 'for',
  'but', 'and', 'or', 'so', 'if', 'as',
]);
const normalizeWord = (word = "") =>
  word
    .toString()
    .replace(/[’`]/g, "'")
    .toLowerCase()
    .trim()
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "");
export default function PronunciationLessonPage({ API_BASE, defaultLessonFlowId = "pronunciation-lesson" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fromWeek = location.state?.fromWeek ?? null;

  const { sequences, error: sequenceError, loading: sequencesLoading } =
    useSequences();
  const { lessons, error: lessonError, loading: lessonsLoading } =
    useLessonFlow();
  const [activeLessonId, setActiveLessonId] = useState(defaultLessonFlowId);
  const activeLesson = useMemo(() => {
    return lessons.find(l => l.metadata.id === activeLessonId) ?? null;
  }, [lessons, activeLessonId]);

  const [currentStateId, setCurrentStateId] = useState("");
  const [activeSequenceName, setActiveSequenceName] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  const [sequenceAutoPlayToken, setSequenceAutoPlayToken] = useState(0);
  const [sequenceStopToken, setSequenceStopToken] = useState(0);
  const [lessonDynamicData, setLessonDynamicData] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState("");
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [multiAudioIndex, setMultiAudioIndex] = useState(0);
  const [isMultiAudioPlayback, setIsMultiAudioPlayback] = useState(false);
  const activeAudioFilesRef = useRef([]);
  const playbackTimeoutRef = useRef(null);

  const requestedLessonId = useMemo(() => {
    const flowFromLesson = location.state?.lesson?.lesson_flow_id;
    const flowFromState = location.state?.lessonFlowId;
    return flowFromLesson ?? flowFromState ?? defaultLessonFlowId;
  }, [
    location.state?.lesson?.lesson_flow_id,
    location.state?.lessonFlowId,
    defaultLessonFlowId
  ]);

  useEffect(() => {
    if (!lessons.length) return;
    const availableIds = lessons.map(l => l.metadata.id);
    if (availableIds.includes(requestedLessonId)) {
      setActiveLessonId(requestedLessonId);
    } else if (availableIds.includes(defaultLessonFlowId)) {
      setActiveLessonId(defaultLessonFlowId);
    } else if (lessons[0]) {
      setActiveLessonId(lessons[0].metadata.id);
    }
  }, [lessons, requestedLessonId, defaultLessonFlowId]);

  // 録音機能の状態
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [recordingError, setRecordingError] = useState(null);

  const skipStopAudioRef = useRef(false);
  const pendingAutoPlayStateRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const countdownTimerRef = useRef(null);

  const confidenceLookup = useMemo(() => {
    const bySourceId = new Map();
    const byNormalized = new Map();
    if (Array.isArray(analysisResult?.words)) {
      analysisResult.words.forEach((word) => {
        if (!word || word.ignore_for_scoring) {
          return;
        }
        if (typeof word.confidence !== "number") {
          return;
        }
        if (word.source_token_id !== undefined && word.source_token_id !== null) {
          const sourceKey = String(word.source_token_id);
          if (!bySourceId.has(sourceKey)) {
            bySourceId.set(sourceKey, word.confidence);
          }
        }
        const normalizedKey =
          typeof word.normalized === "string" && word.normalized
            ? word.normalized
            : normalizeWord(word.word ?? "");
        if (normalizedKey && !byNormalized.has(normalizedKey)) {
          byNormalized.set(normalizedKey, word.confidence);
        }
      });
    }
    return { bySourceId, byNormalized };
  }, [analysisResult]);

  const resolveConfidence = useCallback(
    (alignmentItem) => {
      if (!alignmentItem || !Array.isArray(analysisResult?.words)) {
        return null;
      }
      const { bySourceId, byNormalized } = confidenceLookup;
      const sourceId = alignmentItem.source_token_id;
      if (sourceId !== undefined && sourceId !== null) {
        const key = String(sourceId);
        if (bySourceId.has(key)) {
          return bySourceId.get(key);
        }
      }
      const normalizedKey =
        typeof alignmentItem.normalized === "string" && alignmentItem.normalized
          ? alignmentItem.normalized
          : normalizeWord(alignmentItem.hyp_word ?? "");
      if (normalizedKey && byNormalized.has(normalizedKey)) {
        return byNormalized.get(normalizedKey);
      }
      if (normalizedKey) {
        const fallback = analysisResult.words.find(
          (word) =>
            word &&
            !word.ignore_for_scoring &&
            normalizeWord(word.word ?? "") === normalizedKey
        );
        if (fallback && typeof fallback.confidence === "number") {
          return fallback.confidence;
        }
      }
      if (typeof alignmentItem?.confidence === "number") {
        return alignmentItem.confidence;
      }
      return null;
    },
    [analysisResult, confidenceLookup]
  );

  const currentState = useMemo(() => {
    if (!activeLesson || !currentStateId) return null;
    return activeLesson.states[currentStateId] ?? null;
  }, [activeLesson, currentStateId]);

  useEffect(() => {
    if (!activeLesson) {
      setCurrentStateId("");
      setActiveSequenceName(null);
      return;
    }

    const initial =
      activeLesson.metadata.initialState ||
      activeLesson.initialState ||
      Object.keys(activeLesson.states)[0] ||
      "";

    setCurrentStateId(initial);
    setActiveSequenceName(activeLesson.metadata.defaultSequence ?? null);
  }, [activeLesson]);

  useEffect(() => {
    if (location.state?.lesson) {
      setLessonDynamicData({ lesson: location.state.lesson });
      setDataLoading(false);
    } else {
      setDataError("レッスンデータが見つかりません。前のページに戻ってください。");
      setDataLoading(false);
      setTimeout(() => {
        navigate('/pronunciation_lab');
      }, 5000);
    }
  }, [location.state, navigate]);

  const stopAllAudio = useCallback((options = {}) => {
    if (playbackTimeoutRef.current) {
      clearTimeout(playbackTimeoutRef.current);
      playbackTimeoutRef.current = null;
    }
    setIsMultiAudioPlayback(false);
    setMultiAudioIndex(0);
    if (!options.skipSequenceStopToken) {
      setSequenceStopToken(prev => prev + 1);
    }
  }, []);

  useEffect(() => () => {
    stopAllAudio();
    // アンマウント時にストリームもクリーンアップ
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, [stopAllAudio]);

  useEffect(() => {
    if (!currentStateId) {
      return;
    }
    stopAllAudio();
  }, [currentStateId, stopAllAudio]);

  const lessonAudioFiles = useMemo(() => {
    const raw = lessonDynamicData?.lesson?.audio_file;
    if (Array.isArray(raw)) {
      return raw
        .filter(file => typeof file === "string")
        .map(file => file.trim())
        .filter(file => file !== "");
    }
    if (typeof raw === "string" && raw.trim() !== "") {
      return [raw.trim()];
    }
    return [];
  }, [lessonDynamicData]);

  const getByPath = useCallback(
    path => {
      if (!path) return undefined;
      const segments = path.split(".");
      let current = lessonDynamicData;
      for (const segment of segments) {
        if (current == null || typeof current !== "object") {
          return undefined;
        }
        current = current[segment];
      }
      return current;
    },
    [lessonDynamicData]
  );

  const resolveTextValue = useCallback(
    (value, options = {}) => {
      if (!value) {
        return options.defaultValue ?? "";
      }

      const trimmed = value.trim();
      if (trimmed === "") {
        return options.defaultValue ?? "";
      }

      const first = trimmed.charAt(0);
      const last = trimmed.charAt(trimmed.length - 1);
      const isQuoted =
        (first === '"' && last === '"') || (first === "'" && last === "'");

      if (isQuoted) {
        return trimmed.slice(1, -1);
      }

      const looksLikeVariable = trimmed.startsWith("lesson.");

      if (!looksLikeVariable) {
        return trimmed;
      }

      const resolved = getByPath(trimmed);
      if (resolved === undefined || resolved === null || resolved === "") {
        return options.fallbackToOriginal
          ? trimmed
          : options.defaultValue ?? "";
      }

      return String(resolved);
    },
    [getByPath]
  );

  const resolveResourceValue = useCallback(
    value => resolveTextValue(value, { defaultValue: "" }),
    [resolveTextValue]
  );

  const handleAutoActions = useCallback(
    actions => {
      if (!actions) return;

      actions.forEach(action => {
        if (!action || typeof action !== "object") return;

        switch (action.type) {
          case "sequence": {
            const resolvedSequence = resolveResourceValue(action.ref);
            if (resolvedSequence) {
              setActiveSequenceName(resolvedSequence);
              setIsAutoPlaying(true);
            }
            break;
          }
          case "expression": {
            console.info("TODO: 設定する表情:", action.name);
            break;
          }
          default:
            console.warn("未対応のautoPlayアクションです:", action);
        }
      });
    },
    [resolveResourceValue]
  );

  const queueAutoPlayForState = useCallback(
    (stateId, { immediate = false } = {}) => {
      if (!stateId || !activeLesson) {
        pendingAutoPlayStateRef.current = null;
        return;
      }

      const targetState = activeLesson.states[stateId];
      if (!targetState) {
        pendingAutoPlayStateRef.current = null;
        return;
      }

      if (immediate) {
        handleAutoActions(targetState.autoPlay);
        pendingAutoPlayStateRef.current = null;
      } else {
        pendingAutoPlayStateRef.current = stateId;
      }
    },
    [activeLesson, handleAutoActions]
  );

  useEffect(() => {
    if (!currentState || !activeLesson) {
      return;
    }

    if (currentState.live2d?.playSequence) {
      setActiveSequenceName(currentState.live2d.playSequence);
    } else if (!activeSequenceName && activeLesson.metadata.defaultSequence) {
      setActiveSequenceName(activeLesson.metadata.defaultSequence);
    }
  }, [currentState, activeLesson, activeSequenceName]);

  useEffect(() => {
    if (!currentStateId) {
      return;
    }

    const shouldSkipStop = skipStopAudioRef.current;
    skipStopAudioRef.current = false;

    if (!shouldSkipStop) {
      stopAllAudio();
    }

    if (pendingAutoPlayStateRef.current === currentStateId) {
      handleAutoActions(currentState?.autoPlay);
      pendingAutoPlayStateRef.current = null;
    }
  }, [currentStateId, currentState, handleAutoActions, stopAllAudio]);

  useEffect(() => {
    if (currentStateId !== "countdown") {
      setCountdown(null);
      // countdown以外の状態になったらタイマーをクリア
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      return;
    }

    // マイク使用許可を求める
    const startCountdownWithMic = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        setRecordingError(null);
      } catch (err) {
        console.error("マイクアクセスエラー:", err);
        setRecordingError("マイクへのアクセスが許可されませんでした。");
        return;
      }

      setCountdown(3);
      const timerId = window.setInterval(() => {
        setCountdown(prev => {
          if (prev == null) return null;
          if (prev <= 1) {
            window.clearInterval(timerId);
            countdownTimerRef.current = null;
            queueAutoPlayForState("recording", { immediate: false });
            setCurrentStateId("recording");
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      countdownTimerRef.current = timerId;
    };

    startCountdownWithMic();

    return () => {
      // クリーンアップ: タイマーを必ずクリア
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [currentStateId, queueAutoPlayForState]);

  useEffect(() => {
    if (currentStateId !== "recording") {
      setRecordingElapsed(0);
      return;
    }

    // 録音開始
    const startRecording = () => {
      if (!streamRef.current) {
        console.error("ストリームが利用できません");
        setRecordingError("録音を開始できませんでした。");
        return;
      }

      const mediaRecorder = new MediaRecorder(streamRef.current);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });

        // 録音停止後すぐにresultPendingへ遷移
        console.log("📤 録音停止、resultPendingへ遷移");
        queueAutoPlayForState("resultPending", { immediate: true });
        setCurrentStateId("resultPending");

        // その後、評価処理を開始
        await sendAudioToServer(audioBlob);

        // ストリームを停止
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start();
    };

    startRecording();
    setRecordingElapsed(0);

    const intervalId = window.setInterval(
      () => setRecordingElapsed(prev => prev + 1),
      1000
    );
    const timeoutId = window.setTimeout(
      () => {
        // 自動停止
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
          mediaRecorderRef.current.stop();
        }
      },
      7000
    );

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);

      // クリーンアップ時に録音を停止
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    };
  }, [currentStateId, queueAutoPlayForState]);

  // 合格判定関数（PronunciationLabPageから移植）
  const checkPassStatus = useCallback((result) => {
    if (!result || !result.alignment || !result.alignment.alignments) {
      return false;
    }

    const alignments = result.alignment.alignments || [];
    const filteredAlignments = alignments.filter(item => !item?.ignore_for_scoring);

    // すべての単語が完全一致か確認
    const allMatch = filteredAlignments.every(item => item.status === "match");
    if (!allMatch) {
      return false;
    }

    const words = Array.isArray(result?.words) ? result.words : [];
    const bySourceId = new Map();
    const byNormalized = new Map();
    words.forEach((word) => {
      if (!word || word.ignore_for_scoring) {
        return;
      }
      if (typeof word.confidence !== "number") {
        return;
      }
      if (word.source_token_id !== undefined && word.source_token_id !== null) {
        const sourceKey = String(word.source_token_id);
        if (!bySourceId.has(sourceKey)) {
          bySourceId.set(sourceKey, word.confidence);
        }
      }
      const normalizedKey =
        typeof word.normalized === "string" && word.normalized
          ? word.normalized
          : normalizeWord(word.word ?? "");
      if (normalizedKey && !byNormalized.has(normalizedKey)) {
        byNormalized.set(normalizedKey, word.confidence);
      }
    });

    // すべての単語の発音信頼度が0.4以上か確認（EXCLUDED_WORDSは除外）
    for (const item of filteredAlignments) {
      if (!item.hyp_word) {
        continue;
      }
      const normalizedHyp =
        typeof item.normalized === "string" && item.normalized
          ? item.normalized
          : normalizeWord(item.hyp_word);
      if (!normalizedHyp || EXCLUDED_WORDS.has(normalizedHyp)) {
        continue;
      }

      let confidence = null;
      if (item.source_token_id !== undefined && item.source_token_id !== null) {
        const key = String(item.source_token_id);
        if (bySourceId.has(key)) {
          confidence = bySourceId.get(key);
        }
      }
      if (confidence === null && normalizedHyp && byNormalized.has(normalizedHyp)) {
        confidence = byNormalized.get(normalizedHyp);
      }
      if (confidence === null && normalizedHyp) {
        const fallback = words.find(
          (word) =>
            word &&
            !word.ignore_for_scoring &&
            normalizeWord(word.word ?? "") === normalizedHyp
        );
        if (fallback && typeof fallback.confidence === "number") {
          confidence = fallback.confidence;
        }
      }
      if (confidence !== null && confidence < 0.4) {
        return false;
      }
    }

    return true;
  }, []);

  // 音声をサーバーに送信して評価（PronunciationLabPageから移植）
  const sendAudioToServer = useCallback(async (audioBlob) => {
    console.log("🎤 sendAudioToServer開始", { blobSize: audioBlob.size });
    setIsAnalyzing(true);
    setRecordingError(null);

    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      const activityType = lessonDynamicData?.lesson?.is_exam ? "memorization_exam" : "guided_practice";
      formData.append("activity_type", activityType);

      if (user?.db_user_id !== undefined && user?.db_user_id !== null) {
        formData.append("user_id", String(user.db_user_id));
      }

      if (typeof recordingElapsed === "number" && !Number.isNaN(recordingElapsed)) {
        formData.append("recording_seconds", String(recordingElapsed));
      }

      if (activeLessonId) {
        formData.append("lesson_flow_id", activeLessonId);
      }

      if (fromWeek !== null && fromWeek !== undefined && fromWeek !== "") {
        formData.append("source_week", String(fromWeek));
      }

      // lessonDynamicDataからsentence_enを取得
      const referenceText = lessonDynamicData?.lesson?.sentence_en;
      console.log("📝 参照テキスト:", referenceText);
      if (referenceText) {
        formData.append("reference_text", referenceText);
      }
      const recognizedSentenceId = lessonDynamicData?.lesson?.recognized_sentence_id;
      if (recognizedSentenceId) {
        formData.append("recognized_sentence_id", String(recognizedSentenceId));
      }

      const apiUrl = API_BASE ? `${API_BASE}/pronunciation/analyze` : "/api/pronunciation/analyze";
      console.log("🌐 APIリクエスト送信:", apiUrl);
      const response = await fetch(apiUrl, {
        method: "POST",
        body: formData,
      });

      console.log("📡 APIレスポンス受信:", { status: response.status, ok: response.ok });

      if (!response.ok) {
        throw new Error(`サーバーエラー: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ 評価結果:", result);

      if (result.no_speech_detected) {
        setActiveSequenceName("lesson_result_error");
        setAnalysisResult(null);
        console.log("⚠️ 音声未検出のため中断");
        queueAutoPlayForState("microphoneError", { immediate: true });
        setCurrentStateId("microphoneError");
        return;
      }

      const nextResult = { ...result };
      if (result.logging_error) {
        setRecordingError("分析結果の保存に失敗しました。時間をおいて再試行してください。");
      }
      setAnalysisResult(nextResult);

      let isPassed = false;

      // 合否判定
      isPassed = checkPassStatus(result);
      console.log("🎯 合否判定:", isPassed ? "合格" : "不合格");

      // 結果に応じて状態遷移
      if (isPassed) {
        console.log("🎉 resultPassedへ遷移");
        setActiveSequenceName("lesson_result_pass");
        setIsAutoPlaying(true); // 自動再生トリガーを発火
        queueAutoPlayForState("resultPassed", { immediate: true });
        setCurrentStateId("resultPassed");
      } else {
        console.log("😔 resultFailedへ遷移");
        setActiveSequenceName("lesson_result_fail");
        setIsAutoPlaying(true); // 自動再生トリガーを発火
        queueAutoPlayForState("resultFailed", { immediate: true });
        setCurrentStateId("resultFailed");
      }
    } catch (err) {
      console.error("❌ 音声送信エラー:", err);
      setRecordingError("音声の分析中にエラーが発生しました。");
      // エラー時はresultPendingへ
      queueAutoPlayForState("resultPending", { immediate: true });
      setCurrentStateId("resultPending");
    } finally {
      console.log("🏁 sendAudioToServer終了");
      setIsAnalyzing(false);
    }
  }, [API_BASE, lessonDynamicData, queueAutoPlayForState, user, recordingElapsed, activeLessonId, fromWeek, checkPassStatus]);

  const handleAction = useCallback(
    action => {
      switch (action) {
        case "start_countdown":
          skipStopAudioRef.current = true;
          queueAutoPlayForState("countdown", { immediate: true });
          setCurrentStateId("countdown");
          return true;
        case "cancel_countdown":
          // カウントダウンキャンセル時はストリームを停止
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
          }
          skipStopAudioRef.current = true;
          queueAutoPlayForState("practicePrep", { immediate: true });
          setCurrentStateId("practicePrep");
          return true;
        case "finish_recording":
          skipStopAudioRef.current = true;
          queueAutoPlayForState("resultPending", { immediate: true });
          setCurrentStateId("resultPending");
          return true;
        case "result_passed":
          skipStopAudioRef.current = true;
          queueAutoPlayForState("resultPassed", { immediate: true });
          setCurrentStateId("resultPassed");
          return true;
        case "result_failed":
          skipStopAudioRef.current = true;
          queueAutoPlayForState("resultFailed", { immediate: true });
          setCurrentStateId("resultFailed");
          return true;
        case "replay_sentence":
          handleAutoActions(currentState?.autoPlay);
          return false;
        default:
          console.warn("未対応のアクションです:", action);
          return false;
      }
    },
    [currentState, handleAutoActions, queueAutoPlayForState]
  );

  const handleButtonClick = useCallback(
    button => {
      const nextState =
        button.next && activeLesson ? activeLesson.states[button.next] ?? null : null;
      const actionTriggersAuto =
        button.action && ACTIONS_TRIGGERING_AUTOPLAY.has(button.action);
      const buttonAutoPlaysAudio =
        Array.isArray(button.autoPlay) &&
        button.autoPlay.some(
          action => action && (action.type === "sequence" || action.type === "audio")
        );
      const willImmediateAuto =
        !!nextState && Array.isArray(nextState.autoPlay) && nextState.autoPlay.length > 0;

      const shouldSkipStopToken = buttonAutoPlaysAudio || willImmediateAuto || actionTriggersAuto;

      stopAllAudio({ skipSequenceStopToken: shouldSkipStopToken });
      handleAutoActions(button.autoPlay);

      let handled = false;
      if (button.action) {
        handled = handleAction(button.action);
      }

      if (button.next && !handled) {
        skipStopAudioRef.current = true;
        queueAutoPlayForState(button.next, { immediate: true });
        setCurrentStateId(button.next);
      }
    },
    [
      activeLesson,
      handleAction,
      handleAutoActions,
      queueAutoPlayForState,
      stopAllAudio
    ]
  );

  const renderButtons = useCallback(
    (buttons = []) => {
      if (!Array.isArray(buttons) || buttons.length === 0) {
        return null;
      }
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", width: "100%" }}>
          {buttons.map((button, index) => {
            const variant =
              button.style ?? (index === buttons.length - 1 ? "primary" : "secondary");
            const style = buttonStyleMap[variant] ?? buttonBaseStyle;
            return (
              <button
                key={`${button.label}-${index}`}
                style={style}
                onClick={() => handleButtonClick(button)}
              >
                {button.label}
              </button>
            );
          })}
        </div>
      );
    },
    [handleButtonClick]
  );

  const resolvedActiveSequence = useMemo(() => {
    const baseSequence = activeSequenceName
      ? sequences.find(s => s.name === activeSequenceName)
      : null;
    if (!baseSequence) return null;

    const hasExplicitAudio = !!baseSequence.audioFileExplicit;
    const resolvedValue = resolveResourceValue(baseSequence.audioFile);
    let files = Array.isArray(resolvedValue)
      ? resolvedValue
          .filter(file => typeof file === "string")
          .map(file => file.trim())
          .filter(file => file !== "")
      : (typeof resolvedValue === "string" && resolvedValue.trim() !== "" ? [resolvedValue.trim()] : []);

    if (!files.length && !hasExplicitAudio && lessonAudioFiles.length) {
      files = lessonAudioFiles;
    }

    if (!files.length) {
      return {
        ...baseSequence,
        audioFile: "",
        multiAudioFiles: [],
        isMultiAudio: false,
      };
    }

    let playbackFiles = files;
    let audioQuery = "";
    if (files.length > 1) {
      const match = files[0].match(/^(.*_)(\d+)(\.[^./]+)$/);
      if (match) {
        playbackFiles = [`${match[1]}0${match[3]}`];
      } else {
        playbackFiles = [files[0]];
      }
    }

    const clampedIndex = Math.min(multiAudioIndex, playbackFiles.length - 1);
    const resolvedAudioFile = playbackFiles[clampedIndex] ?? playbackFiles[0];
    audioQuery = "voice=avatar";

    return {
      ...baseSequence,
      audioFile: resolvedAudioFile,
      audioQuery,
      multiAudioFiles: playbackFiles,
      isMultiAudio: playbackFiles.length > 1,
    };
  }, [activeSequenceName, sequences, resolveResourceValue, lessonAudioFiles, multiAudioIndex]);

  const playAudioAt = useCallback((index) => {
    const files = activeAudioFilesRef.current ?? [];
    if (!files.length) {
      return;
    }
    const bounded = Math.max(0, Math.min(index, files.length - 1));
    setMultiAudioIndex(bounded);
    setSequenceAutoPlayToken(prev => prev + 1);
  }, []);

  useEffect(() => {
    const files = resolvedActiveSequence?.multiAudioFiles ?? [];
    activeAudioFilesRef.current = files.length ? files : lessonAudioFiles;
  }, [resolvedActiveSequence, lessonAudioFiles]);

  useEffect(() => {
    if (!isAutoPlaying || !resolvedActiveSequence) {
      return;
    }

    setIsMultiAudioPlayback((activeAudioFilesRef.current ?? []).length > 1);
    if (playbackTimeoutRef.current) {
      clearTimeout(playbackTimeoutRef.current);
      playbackTimeoutRef.current = null;
    }
    playAudioAt(0);
    setIsAutoPlaying(false);
  }, [isAutoPlaying, resolvedActiveSequence, lessonAudioFiles, playAudioAt]);

  useEffect(() => {
    if (!activeSequenceName || !sequences.length) return;
    if (!resolvedActiveSequence) {
      console.warn(
        `指定されたシーケンス "${activeSequenceName}" は見つかりませんでした`
      );
    }
  }, [activeSequenceName, sequences, resolvedActiveSequence]);

  const handleSequenceEnded = useCallback(() => {
    const files = activeAudioFilesRef.current ?? [];

    if (!files.length) {
      setIsMultiAudioPlayback(false);
      setMultiAudioIndex(0);
      return;
    }

    if (!isMultiAudioPlayback || files.length <= 1) {
      setIsMultiAudioPlayback(false);
      setMultiAudioIndex(0);
      return;
    }

    const nextIndex = multiAudioIndex + 1;
    if (nextIndex >= files.length) {
      setIsMultiAudioPlayback(false);
      return;
    }

    if (playbackTimeoutRef.current) {
      return;
    }

    playbackTimeoutRef.current = setTimeout(() => {
      playbackTimeoutRef.current = null;
      playAudioAt(nextIndex);
    }, 500);
  }, [isMultiAudioPlayback, multiAudioIndex, playAudioAt]);

  useEffect(() => {
    return () => {
      if (playbackTimeoutRef.current) {
        clearTimeout(playbackTimeoutRef.current);
        playbackTimeoutRef.current = null;
      }
    };
  }, []);

  // 評価テキストを取得する関数（PronunciationLabPageから移植）
  const getEvaluationText = useCallback((item) => {
    if (item?.is_proper_noun || item?.ignore_for_scoring || item?.ignored_due_to_proper) {
      if (item?.is_proper_noun) {
        return "採点外";
      }
      return "";
    }
    if (item.status === "deletion") {
      return "聞き取れませんでした";
    }
    if (item.status === "insertion" || item.status === "error") {
      return "間違いです";
    }
    if (item.status === "match") {
      const normalizedHyp =
        typeof item.normalized === "string" && item.normalized
          ? item.normalized
          : normalizeWord(item.hyp_word ?? "");
      let confidence = resolveConfidence(item);

      if (normalizedHyp && EXCLUDED_WORDS.has(normalizedHyp)) {
        confidence = Math.max(confidence ?? 0, 0.6);
      }

      if (confidence !== null) {
        if (confidence >= 0.6) {
          return "完璧";
        } else if (confidence >= 0.4) {
          return "上手";
        } else {
          return "聞き取りにくいです";
        }
      }
    }
    return "-";
  }, [resolveConfidence]);

  const overlayElements = useMemo(() => {
    if (!currentState) {
      return {
        title: "設定された状態が見つかりません。",
        sentence: "",
        card: (
          <div style={baseCardStyle}>
            <div style={bodyStyle}>レッスン定義をご確認ください。</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {renderButtons([])}
            </div>
          </div>
        )
      };
    }

    const overlay = currentState.overlay ?? {};

    const titleText = resolveTextValue(overlay.title, {
      fallbackToOriginal: true
    });
    const sentenceText = resolveTextValue(overlay.sentence, {
      fallbackToOriginal: true
    });

    const infoNodes = [];
    const warningText = resolveTextValue(overlay.warning, {
      fallbackToOriginal: true
    });
    if (warningText) {
      infoNodes.push(
        <div key="warning" style={{ ...bodyStyle, color: "#f87171" }}>
          {warningText}
        </div>
      );
    }

    const bodyText = resolveTextValue(overlay.body, {
      fallbackToOriginal: true
    });
    if (bodyText) {
      infoNodes.push(
        <div key="body" style={bodyStyle}>
          {bodyText}
        </div>
      );
    }

    if (currentStateId === "countdown" && countdown != null) {
      infoNodes.push(
        <div
          key="countdown"
          style={{ fontSize: "42px", fontWeight: "bold", textAlign: "center" }}
        >
          {countdown}
        </div>
      );
    }

    if (currentStateId === "recording") {
      infoNodes.push(
        <div
          key="recording"
          style={{ fontSize: "16px", textAlign: "center" }}
        >
          録音中… {recordingElapsed} 秒
        </div>
      );
    }

    // 評価詳細テーブル表示（透明オーバーレイ）
    if (currentStateId === "resultDetail" && analysisResult) {
      const alignments = analysisResult.alignment?.alignments || [];
      infoNodes.push(
        <div
          key="evaluation-table"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            backdropFilter: "blur(2px)",
            borderRadius: "12px",
            padding: "12px",
            maxHeight: "400px",
            overflowY: "auto",
            color: "#ffffff",
            textShadow: "0 1px 3px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 0, 0, 0.5)"
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ padding: "8px", textAlign: "left", borderBottom: "2px solid rgba(255, 255, 255, 0.5)" }}>例文</th>
                <th style={{ padding: "8px", textAlign: "left", borderBottom: "2px solid rgba(255, 255, 255, 0.5)" }}>発話</th>
                <th style={{ padding: "8px", textAlign: "left", borderBottom: "2px solid rgba(255, 255, 255, 0.5)" }}>評価</th>
              </tr>
            </thead>
            <tbody>
              {alignments
                .filter(item => !item?.ignored_due_to_proper)
                .map((item, idx) => {
                  const evaluationText = getEvaluationText(item);
                  let evaluationColor = "#ffffff";
                  if (evaluationText === "完璧") {
                    evaluationColor = "#4CAF50";
                  } else if (evaluationText === "上手") {
                    evaluationColor = "#8BC34A";
                  } else if (evaluationText === "聞き取りにくいです") {
                    evaluationColor = "#FF9800";
                  } else if (evaluationText === "聞き取れませんでした" || evaluationText === "間違いです") {
                    evaluationColor = "#f44336";
                  } else if (evaluationText === "採点外") {
                    evaluationColor = "#666";
                  }

                return (
                  <tr key={idx}>
                    <td style={{ padding: "8px", borderBottom: "1px solid rgba(255, 255, 255, 0.2)" }}>
                      {item.ref_word || "-"}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid rgba(255, 255, 255, 0.2)" }}>
                      {item.hyp_word || "-"}
                    </td>
                    <td style={{
                      padding: "8px",
                      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
                      color: evaluationColor,
                      fontWeight: "bold"
                    }}>
                      {evaluationText}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    // エラー表示
    if (recordingError) {
      infoNodes.push(
        <div
          key="recording-error"
          style={{ fontSize: "14px", color: "#fca5a5", textAlign: "center" }}
        >
          {recordingError}
        </div>
      );
    }

    const hasInfo = infoNodes.length > 0;

    const cardNode = (
      <div
        style={{
          ...baseCardStyle,
          gap: "12px",
          minHeight: hasInfo ? "160px" : undefined,
          justifyContent: hasInfo ? "flex-start" : "center"
        }}
      >
        {hasInfo && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {infoNodes}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginTop: hasInfo ? "12px" : 0
          }}
        >
          {renderButtons(currentState.buttons)}
        </div>
      </div>
    );

    return { title: titleText, sentence: sentenceText, card: cardNode };
  }, [
    currentState,
    currentStateId,
    countdown,
    recordingElapsed,
    analysisResult,
    recordingError,
    getEvaluationText,
    renderButtons,
    resolveTextValue
  ]);

  const combinedError = [lessonError, sequenceError, dataError]
    .filter(Boolean)
    .join("\n");

  const isLoading =
    lessonsLoading || sequencesLoading || dataLoading;

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        background:
          "linear-gradient(180deg, #0f172a 0%, #1f2937 60%, #111827 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "16px 12px",
        boxSizing: "border-box",
        gap: "16px"
      }}
    >
      <header
        style={{
          width: "100%",
          maxWidth: "440px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#e0f2fe"
        }}
      >
        <h1 style={{ margin: 0, fontSize: "18px" }}>
          {activeLesson?.metadata.title ?? "発音練習モード"}
        </h1>
        <Link
          to="/pronunciation_lab"
          state={fromWeek ? { selectedWeek: fromWeek } : undefined}
          style={{
            color: "#60a5fa",
            textDecoration: "none",
            fontSize: "14px"
          }}
        >
          一覧へ戻る
        </Link>
      </header>

      {combinedError && (
        <div
          style={{
            color: "#fca5a5",
            fontSize: "13px",
            maxWidth: "440px",
            width: "100%",
            whiteSpace: "pre-wrap"
          }}
        >
          {combinedError}
        </div>
      )}

      {isLoading && (
        <div style={{ color: "#cbd5f5", fontSize: "13px" }}>
          データを読み込み中…
        </div>
      )}

      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "420px",
          height: "clamp(800px, 100vw * 1.1, 850px)",
          boxSizing: "border-box",
          display: "flex"
        }}
      >
        <Live2DViewer
          sequence={resolvedActiveSequence}
          displayLabel={
            resolvedActiveSequence?.name ??
            activeLesson?.metadata.title ??
            "Lesson Sequence"
          }
          autoPlayToken={sequenceAutoPlayToken}
          stopToken={sequenceStopToken}
          onSequenceEnded={handleSequenceEnded}
          overlay={
            <div
              style={{
                position: "relative",
                height: "100%",
                width: "100%",
                padding: "16px",
                boxSizing: "border-box"
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "16px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "calc(100% - 32px)",
                  maxWidth: "360px",
                  textAlign: "center"
                }}
              >
                {overlayElements.title && (
                  <div style={titleStyle}>{overlayElements.title}</div>
                )}
              </div>
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "calc(100% - 32px)",
                  maxWidth: "360px",
                  textAlign: "center"
                }}
              >
                {overlayElements.sentence && (
                  <div style={sentenceStyle}>{overlayElements.sentence}</div>
                )}
              </div>
              <div
                style={{
                  position: "absolute",
                  bottom: "16px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "calc(100% - 32px)",
                  maxWidth: "360px"
                }}
              >
                {overlayElements.card}
              </div>
            </div>
          }
        />
      </div>

      <div
        style={{
          color: "#9ca3af",
          fontSize: "12px",
          maxWidth: "440px",
          textAlign: "center"
        }}
      >
        ※ 現時点ではテキスト表示と遷移、音声再生、リップシンク再生までをサポートしています。
      </div>
    </div>
  );
}
